import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Autocomplete, Button, ButtonContainer, DatePicker, MenuItem, ScrollableDrawer, Stack, Select, TextField, useCurrentBreakpoint, Typography } from '@emburse/embark-core';
import dayjs from 'dayjs';
import { Checkbox, EmployeeSearch, EmpUDFSearch } from '../../../components';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import { emailStatuses, welcomeEmails, adminLevels, empTypes, AccountStatus } from '../../../interfaces/ManageUsers';
import { EmpUDF } from '../../../interfaces/UDF';
import { Employee, EmpType } from '../../../interfaces/Employee';
import { dateAdapterInstance } from '../../../util/dateAdapter';
const { formats } = dateAdapterInstance;
const ManageUsersFiltersDrawer = forwardRef(({ open, departments, filterParameters, accountConfig, gld1, gld2, gld3, gld4, gld5, approvalLimits, isTreasurerAvailable, openUpdated, onCancel, onSave }, ref) => {
    useTranslation();
    const currentBreakpoint = useCurrentBreakpoint();
    const [accountStatus, setAccountStatus] = useState(AccountStatus.Active);
    const [userEmail, setUserEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [empID, setEmpID] = useState('');
    const [emailStatus, setEmailStatus] = useState(null);
    const [departmentName, setDepartmentName] = useState('');
    const [departmentPK, setDepartmentPK] = useState(null);
    const [adminLevel, setAdminLevel] = useState('');
    const [role, setRole] = useState('');
    const [auditor, setAuditor] = useState(null);
    const [approverEmail, setApproverEmail] = useState('');
    const [approverPK, setApproverPK] = useState(0);
    const [accountantEmail, setAccountantEmail] = useState('');
    const [accountantPK, setAccountantPK] = useState(0);
    const [dateCreatedStart, setDateCreatedStart] = useState(null);
    const [dateCreatedEnd, setDateCreatedEnd] = useState(null);
    const [approver, setApprover] = useState(new Employee());
    const [accountant, setAccountant] = useState(new Employee());
    const [approvalLimit, setApprovalLimit] = useState(null);
    const [welcomeEmailSent, setWelcomeEmailSent] = useState(null);
    const [treasurer, setTreasurer] = useState(null);
    const [translator, setTranslator] = useState(null);
    const [accountsPayable, setAccountsPayable] = useState(null);
    const [empUDF1PK, setEmpUDF1PK] = useState(0);
    const [empUDF2PK, setEmpUDF2PK] = useState(0);
    const [empUDF3PK, setEmpUDF3PK] = useState(0);
    const [empUDF4PK, setEmpUDF4PK] = useState(0);
    const [empUDF5PK, setEmpUDF5PK] = useState(0);
    const [empUDF1Name, setEmpUDF1Name] = useState('');
    const [empUDF2Name, setEmpUDF2Name] = useState('');
    const [empUDF3Name, setEmpUDF3Name] = useState('');
    const [empUDF4Name, setEmpUDF4Name] = useState('');
    const [empUDF5Name, setEmpUDF5Name] = useState('');
    const setFilters = useCallback(() => {
        setAccountStatus(filterParameters.AccountStatus);
        setUserEmail(filterParameters.UserEmail);
        setFirstName(filterParameters.FirstName);
        setLastName(filterParameters.LastName);
        setEmpID(filterParameters.EmployeeID);
        setEmailStatus(filterParameters.EmailStatus);
        setDepartmentName(filterParameters.DepartmentName);
        setDepartmentPK(filterParameters.DepartmentPK ? filterParameters.DepartmentPK : null);
        setAdminLevel(filterParameters.AdminLevel);
        setRole(filterParameters.Role);
        setAuditor(filterParameters.Auditor);
        setApproverEmail(filterParameters.ApproverEmail);
        setApproverPK(filterParameters.ApproverPK);
        setAccountantEmail(filterParameters.AccountantEmail);
        setAccountantPK(filterParameters.AccountantPK);
        setDateCreatedStart(filterParameters.DateCreatedStart ? dayjs(filterParameters.DateCreatedStart) : null);
        setDateCreatedEnd(filterParameters.DateCreatedEnd ? dayjs(filterParameters.DateCreatedEnd) : null);
        setApprover(new Employee({
            PK: filterParameters.ApproverPK,
            Email: filterParameters.ApproverEmail
        }));
        setAccountant(new Employee({
            PK: filterParameters.AccountantPK,
            Email: filterParameters.AccountantEmail
        }));
        setApprovalLimit(filterParameters.ApprLimitPK || null);
        setWelcomeEmailSent(filterParameters.WelcomeEmailSent);
        setTreasurer(filterParameters.Treasurer);
        setTranslator(filterParameters.Translator);
        setAccountsPayable(filterParameters.AccountsPayable);
        setEmpUDF1PK(filterParameters.EmpUDF1PK);
        setEmpUDF2PK(filterParameters.EmpUDF2PK);
        setEmpUDF3PK(filterParameters.EmpUDF3PK);
        setEmpUDF4PK(filterParameters.EmpUDF4PK);
        setEmpUDF5PK(filterParameters.EmpUDF5PK);
    }, [filterParameters]);
    useEffect(() => {
        setFilters();
    }, [setFilters, open]);
    const resetFilters = () => {
        setFirstName('');
        setLastName('');
        setUserEmail('');
        setEmpID('');
        setEmailStatus(null);
        setAdminLevel('');
        setRole('');
        setAuditor(null);
        setAccountStatus(AccountStatus.Active);
        setDepartmentName('');
        setDepartmentPK(null);
        setApproverEmail('');
        setApproverPK(0);
        setAccountantEmail('');
        setAccountantPK(0);
        setDateCreatedStart(null);
        setDateCreatedEnd(null);
        setApprover(new Employee());
        setAccountant(new Employee());
        setApprovalLimit(null);
        setWelcomeEmailSent(null);
        setTreasurer(null);
        setTranslator(null);
        setAccountsPayable(null);
        setEmpUDF1PK(0);
        setEmpUDF2PK(0);
        setEmpUDF3PK(0);
        setEmpUDF4PK(0);
        setEmpUDF5PK(0);
        setEmpUDF1Name('');
        setEmpUDF2Name('');
        setEmpUDF3Name('');
        setEmpUDF4Name('');
        setEmpUDF5Name('');
    };
    useImperativeHandle(ref, () => ({
        resetFilters() {
            resetFilters();
        },
        resetApprover() {
            setApproverEmail('');
            setApproverPK(0);
            setApprover(new Employee());
        },
        resetAccountant() {
            setAccountantEmail('');
            setAccountantPK(0);
            setAccountant(new Employee());
        }
    }));
    const fieldChangeHandler = (e, field) => {
        const fieldMap = {
            FirstName: setFirstName,
            LastName: setLastName,
            UserEmail: setUserEmail,
            EmpID: setEmpID,
            EmailStatus: setEmailStatus,
            AdminLevel: setAdminLevel,
            Role: setRole,
            Auditor: setAuditor,
            AccountStatus: setAccountStatus,
            ApproverEmail: setApproverEmail,
            AccountantEmail: setAccountantEmail,
            WelcomeEmailSent: setWelcomeEmailSent
        };
        if (field.includes('Auditor')) {
            fieldMap[field](!!e.target.checked || null);
        }
        else {
            fieldMap[field](e.target.value);
        }
    };
    const applyFilters = () => {
        const filterParams = {
            AccountStatus: AccountStatus[accountStatus],
            UserEmail: userEmail,
            FirstName: firstName,
            LastName: lastName,
            EmployeeID: empID,
            EmailStatus: emailStatus,
            AdminLevel: adminLevel,
            Role: role === 'None' ? '' : role,
            Auditor: auditor,
            DepartmentName: departmentName,
            ApproverEmail: approverEmail,
            AccountantEmail: accountantEmail,
            DateCreatedStart: dateCreatedStart
                ? new Date(dayjs(dateCreatedStart).format(formats.keyboardDate))
                : '',
            DateCreatedEnd: dateCreatedStart
                ? new Date(dayjs(dateCreatedEnd).format(formats.keyboardDate))
                : '',
            UserName: filterParameters.UserName,
            ApproverPK: approverPK,
            AccountantPK: accountantPK,
            DepartmentPK: departmentPK || 0,
            ApprLimitPK: approvalLimit || 0,
            WelcomeEmailSent: welcomeEmailSent,
            Treasurer: treasurer,
            Translator: translator,
            AccountsPayable: accountsPayable,
            EmpUDF1PK: empUDF1PK,
            EmpUDF2PK: empUDF2PK,
            EmpUDF3PK: empUDF3PK,
            EmpUDF4PK: empUDF4PK,
            EmpUDF5PK: empUDF5PK
        };
        onCancel();
        onSave(filterParams);
    };
    const generateGLDs = () => Object.entries(accountConfig.EmpUDFConfig).map(([key, value]) => {
        const gldMap = {
            1: [empUDF1PK, setEmpUDF1PK, gld1, setEmpUDF1Name, empUDF1Name],
            2: [empUDF2PK, setEmpUDF2PK, gld2, setEmpUDF2Name, empUDF2Name],
            3: [empUDF3PK, setEmpUDF3PK, gld3, setEmpUDF3Name, empUDF3Name],
            4: [empUDF4PK, setEmpUDF4PK, gld4, setEmpUDF4Name, empUDF4Name],
            5: [empUDF5PK, setEmpUDF5PK, gld5, setEmpUDF5Name, empUDF5Name]
        };
        if (value.UseSearch) {
            return (_jsx(EmpUDFSearch, { id: `GLD${key}`, label: value.Label, style: { width: '100%' }, value: new EmpUDF({ Name: gldMap[key][4], PK: gldMap[key][0] }), getOptionLabel: (option) => option.Name || gldMap[key][4], onSelect: (val, reason) => {
                    if (reason === 'clear') {
                        gldMap[key][1](0);
                        gldMap[key][3]('');
                    }
                    else {
                        gldMap[key][1](val.PK);
                        gldMap[key][3](val.Name);
                    }
                }, udfIndex: parseInt(key, 10) }, `GLD${key}`));
        }
        return (_jsx(Autocomplete, { id: `GLD${key}`, label: value.Label, style: { width: '100%' }, defaultValue: gldMap[key][0], options: gldMap[key][2], getOptionLabel: (option) => {
                const gldIsOption = gldMap[key][2].find((item) => item.pk === option.pk);
                const gldIsPK = gldMap[key][2].find((item) => item.pk === gldMap[key][0]);
                return gldIsOption?.value || gldIsPK?.value || '';
            }, isOptionEqualToValue: (option, val) => val === option?.pk, onChange: (e, val, reason) => {
                if (reason === 'clear') {
                    gldMap[key][1](0);
                }
                else {
                    gldMap[key][1](val.pk);
                }
            } }, `GLD${key}`));
    });
    const optionLabel = (option) => option.FullName?.length ? `${option.FullName} (${option.Email})` : option?.Email || '';
    return (_jsx(ScrollableDrawer, { anchor: "right", open: open, sideWidth: currentBreakpoint.current === 'xs' ? 380 : 450, disableCloseOnBackdropClick: true, openUpdated: openUpdated, header: $t('Advanced Filters'), footer: _jsxs(ButtonContainer, { alignButtons: "right", children: [_jsx(Button, { variant: "text", onClick: resetFilters, children: $t('Reset') }), _jsx(Button, { onClick: applyFilters, children: $t('Apply Filters') })] }), children: _jsxs(Stack, { spacing: 3, direction: "column", alignItems: "stretch", justifyContent: "space-between", children: [_jsx(Select, { id: "AccountStatus", style: { width: '100%', minWidth: 'auto' }, label: $t('Account Status'), value: accountStatus, onChange: (e) => fieldChangeHandler(e, 'AccountStatus'), children: Object.values(AccountStatus).map((item) => (_jsx(MenuItem, { value: item, children: item }, item))) }), _jsx(TextField, { id: "email", name: "Email", fullWidth: true, value: userEmail, label: $t('Email'), type: "text", inputProps: { autocomplete: 'one-time-code' }, onChange: (e) => fieldChangeHandler(e, 'UserEmail') }), _jsx(TextField, { id: "FirstName", name: "First Name", fullWidth: true, label: $t('First Name'), type: "text", value: firstName, inputProps: { autocomplete: 'one-time-code' }, onChange: (e) => fieldChangeHandler(e, 'FirstName') }), _jsx(TextField, { id: "LastName", name: "Last Name", fullWidth: true, label: $t('Last Name'), type: "text", value: lastName, inputProps: { autocomplete: 'one-time-code' }, onChange: (e) => fieldChangeHandler(e, 'LastName') }), _jsx(TextField, { id: "EmployeeID", name: "Employee ID", fullWidth: true, label: $t('Employee ID'), type: "text", value: empID, onChange: (e) => fieldChangeHandler(e, 'EmpID') }), _jsx(Select, { id: "EmilStatus", style: { width: '100%', minWidth: 'auto' }, label: $t('Email Status'), value: emailStatus, onChange: (e) => fieldChangeHandler(e, 'EmailStatus'), children: emailStatuses.map((item) => (_jsx(MenuItem, { value: item.value, children: item.label }, item.label))) }), _jsx(Select, { id: "WelcomeEmail", style: { width: '100%', minWidth: 'auto' }, label: $t('Welcome Email'), value: welcomeEmailSent, onChange: (e) => fieldChangeHandler(e, 'WelcomeEmailSent'), children: welcomeEmails.map((item) => (_jsx(MenuItem, { value: item.value, children: item.label }, item.label))) }), _jsx(Select, { id: "AdminLevel", style: { width: '100%', minWidth: 'auto' }, label: $t('Administration Level'), value: adminLevel, onChange: (e) => fieldChangeHandler(e, 'AdminLevel'), children: adminLevels.map((item) => (_jsx(MenuItem, { value: item, children: item }, item))) }), accountConfig.UIFlags.UseLockedWorkflow && (_jsx(EmployeeSearch, { id: "ApproverEmail", label: $t('Approver Email'), style: { width: '100%' }, value: approver?.PK ? approver : null, empTypes: [EmpType.Manager, EmpType.Accountant, EmpType.Executive], getOptionLabel: optionLabel, onSelect: (employee, reason) => {
                        if (reason === 'clear') {
                            setApprover(new Employee());
                            setApproverEmail('');
                            setApproverPK(0);
                        }
                        else {
                            setApprover(employee);
                            setApproverEmail(employee.Email);
                            setApproverPK(employee.PK);
                        }
                    } })), accountConfig.UIFlags.UseLockedWorkflow && (_jsx(EmployeeSearch, { id: "AccountantEmail", label: $t('Accountant Email'), style: { width: '100%' }, value: accountant?.PK ? accountant : null, empTypes: [EmpType.Accountant], getOptionLabel: optionLabel, onSelect: (employee, reason) => {
                        if (reason === 'clear') {
                            setAccountant(new Employee());
                            setAccountantEmail('');
                            setAccountantPK(0);
                        }
                        else {
                            setAccountant(employee);
                            setAccountantEmail(employee.Email);
                            setAccountantPK(employee.PK);
                        }
                    } })), _jsx(Select, { id: "Role", style: { width: '100%', minWidth: 'auto' }, label: $t('Role'), value: role, onChange: (e) => fieldChangeHandler(e, 'Role'), children: empTypes(true).map((item) => (_jsx(MenuItem, { value: item.name, children: item.name }, item.name))) }), _jsx(Checkbox, { id: "Auditor", color: "primary", label: $t('Auditor'), checked: auditor, onChange: (e) => fieldChangeHandler(e, 'Auditor') }), isTreasurerAvailable && (_jsx(Checkbox, { id: "Treasurer", color: "primary", label: $t('Treasurer'), checked: treasurer, onChange: (e) => setTreasurer(!!e.target.checked || null) })), accountConfig.UIFlags.UseInvoice && (_jsx(Checkbox, { id: "AccountsPayable", color: "primary", label: $t('Accounts Payable'), checked: accountsPayable, onChange: (e) => setAccountsPayable(!!e.target.checked || null) })), accountConfig.Company_UseMultipleLanguages && (_jsx(Checkbox, { id: "Translator", color: "primary", label: $t('Translator'), checked: translator, onChange: (e) => setTranslator(!!e.target.checked || null) })), approvalLimits.length > 0 && accountConfig.UIFlags.UseLockedWorkflow && (_jsx(Autocomplete, { id: "ApprovalLimit", label: "Approval Limit", style: { width: '100%' }, defaultValue: approvalLimit, options: approvalLimits, getOptionLabel: (option) => {
                        const apprSelected = approvalLimits.find((item) => item.PK === option.PK);
                        const apprLimitIsPK = approvalLimits.find((item) => item.PK === approvalLimit);
                        // eslint-disable-next-line no-nested-ternary
                        return apprSelected
                            ? `${apprSelected?.Name} ${apprSelected?.Amount} ${apprSelected?.CurrencySuffix}`
                            : apprLimitIsPK
                                ? `${apprLimitIsPK?.Name} ${apprLimitIsPK?.Amount} ${apprLimitIsPK?.CurrencySuffix}`
                                : '';
                    }, isOptionEqualToValue: (option, val) => val === option?.PK, onChange: (e, val, reason) => {
                        if (reason === 'clear') {
                            setApprovalLimit(null);
                        }
                        else {
                            setApprovalLimit(val.PK);
                        }
                    } })), _jsx(Select, { id: "DepartmentName", style: { width: '100%', minWidth: 'auto' }, label: $t('Department'), value: departmentPK, onChange: (e, child) => {
                        setDepartmentPK(child.props.value);
                        setDepartmentName(child.props.children);
                    }, children: departments.map((item) => (_jsx(MenuItem, { value: item.PK, children: item.Name }, item.PK))) }), _jsx(Typography, { variant: "body2", children: $t('Employee Information') }), generateGLDs(), _jsx(Typography, { variant: "body2", children: $t('Date Created') }), _jsxs(Stack, { spacing: 3, direction: "row", style: { marginTop: '10px' }, children: [_jsx(DatePicker, { label: $t('From'), value: dateCreatedStart, datePickerType: currentBreakpoint.current === 'xs' ? 'mobile' : 'desktop', format: formats.keyboardDate, onChange: (value) => setDateCreatedStart(value), sx: {
                                '& .MuiInputLabel-root': {
                                    top: '-5px'
                                },
                                '& .MuiInputLabel-shrink': {
                                    top: '0px'
                                }
                            }, slotProps: {
                                textField: {
                                    id: 'StartDate',
                                    name: 'Start Date',
                                    fullWidth: true
                                }
                            } }), _jsx(DatePicker, { label: $t('To'), value: dateCreatedEnd, datePickerType: currentBreakpoint.current === 'xs' ? 'mobile' : 'desktop', format: formats.keyboardDate, onChange: (value) => setDateCreatedEnd(value), sx: {
                                '& .MuiInputLabel-root': {
                                    top: '-5px'
                                },
                                '& .MuiInputLabel-shrink': {
                                    top: '0px'
                                }
                            }, slotProps: {
                                textField: {
                                    id: 'EndDate',
                                    name: 'End Date',
                                    fullWidth: true
                                }
                            } })] })] }) }));
});
export default ManageUsersFiltersDrawer;
